<template>
  <div class="whole-step-container prosthetic-v2-container surgery-container">
    <!-- 위쪽: 새로운 치아 입력 카드 (항상 표시) -->
    <div class="input-card-section">
      <zirconia-tooth-info-form
        ref="inputCard"
        :key="`input-card-${inputCardKey}`"
        :array-index="-1"
        :tooth-info="inputToothInfo"
        :selected-teeth-numbers="selectedTeethNumbers"
        :prev-teeth-nos="prevTeethNos"
        :window-inner-width-greater-than-1000="true"
        :certified-companies="companiesByApi.certifiedCompanies"
        :not-certified-companies="companiesByApi.notCertifiedCompanies"
        :modal-show-history="modals.history"
        @update:tooth-info="uiHandler.updateInputToothInfo"
        @update:modal-show-history="updateModalShowHistory"
        @update-manufacturer-list="updateManufacturerList"
      />

      <!-- 추가하기 버튼 -->
      <div class="add-button-container">
        <a-button
          class="add-card-button"
          type="primary"
          @click="uiHandler.confirmAndAddCard"
        >
          {{ $t("workorder_btn_add") }}
        </a-button>
      </div>
    </div>

    <!-- 아래쪽: 확정된 카드들 (탭으로 표시) -->
    <div v-if="confirmedTeeth.length > 0" class="confirmed-cards-section">
      <!-- 탭 UI -->
      <div class="card-tabs-container">
        <button
          v-for="(tooth, index) in confirmedTeeth"
          :key="`tab-${index}`"
          class="card-tab"
          :class="{ active: currentConfirmedIndex === index }"
          @click="uiHandler.switchConfirmedCard(index)"
        >
          <span class="tab-label">{{ getCardTabLabel(tooth, index) }}</span>
          <button
            class="tab-close-btn"
            @click.stop="uiHandler.removeConfirmedCard(index)"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </button>
      </div>

      <!-- 현재 선택된 확정 카드 표시 (수정 가능) -->
      <div class="confirmed-card-display">
        <zirconia-tooth-info-form
          :ref="`confirmedCard-${currentConfirmedIndex}`"
          :key="`confirmed-${currentConfirmedIndex}`"
          :array-index="currentConfirmedIndex"
          :tooth-info="confirmedTeeth[currentConfirmedIndex]"
          :selected-teeth-numbers="
            getOtherConfirmedTeethNumbers(currentConfirmedIndex)
          "
          :prev-teeth-nos="prevTeethNos"
          :window-inner-width-greater-than-1000="true"
          :certified-companies="companiesByApi.certifiedCompanies"
          :not-certified-companies="companiesByApi.notCertifiedCompanies"
          :modal-show-history="modals.history"
          @update:tooth-info="uiHandler.updateConfirmedToothInfo"
          @update:modal-show-history="updateModalShowHistory"
          @update-manufacturer-list="updateManufacturerList"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ZirconiaToothInfoForm from "./ZirconiaToothInfoForm.vue";
import apiInstance from "@/axios";
import { twoBtnDialog, oneBtnDialog } from "@/utils/utils";

function createProstheticTooth() {
  return {
    toothNumber: null,
    toothNumbers: [],
    treatmentType: null,
    group: "",
    itemName: "",
    isCertified: true,
    willTypeProductInfoStepOne: false,
    manufacturerId: "",
    detailSelection: "ZIRCONIA",
    productInfoWithoutCode: {
      brand: "",
      customBrand: "",
      spec: "",
      customSpec: "",
    },
    productInfoWithCode: {
      id: "",
      productCode: "",
      itemName: "",
      description: "",
      gtinCode: "",
      manufacturingDate: "",
      lotNumber: "",
    },
    switches: {
      stm: false,
    },
    inputMode: "barcode",
  };
}

export default {
  name: "Step3",
  components: {
    ZirconiaToothInfoForm,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      inputToothInfo: createProstheticTooth(),
      confirmedTeeth: [],
      currentConfirmedIndex: 0,
      inputCardKey: 0,
      includeInputInPublish: false,
      prevTeethNos: [],
      modals: {
        history: {
          stm: false,
        },
      },
      companiesByApi: {
        certifiedCompanies: [
          {
            name: "덴탈맥스",
            engName: "dentalmax",
            iconUrl:
              "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_round/Zirconia.svg",
            imgUrl:
              "https://certilife.blob.core.windows.net/certilife/202508/file_1754983383092.png",
            colorCode: "#C2E3E3",
            nftTitle: "지르코니아 정품 인증서",
            klipCompanyName: "덴탈맥스(Dentalmax)",
            objectIdKey: "DENTALMAX_OBJECT_ID",
            description:
              "본 카드는 덴탈맥스의 지르코니아 시스템을 정식으로 공급 받아 사용하였음을 인증합니다.",
            displayOrder: 11,
            customFields: {
              itemName: "",
            },
            isCertified: true,
            medicalDept: "DENT",
            productType: "ZIRCONIA",
            deleteYn: "N",
          },
        ],
        notCertifiedCompanies: [
          { name: "기타", engName: "etc", isCertified: false },
        ],
      },
      uiHandler: null,
    };
  },
  computed: {
    selectedTeethNumbers() {
      const allNumbers = [];
      this.confirmedTeeth.forEach((item) => {
        if (item.toothNumbers && item.toothNumbers.length > 0) {
          allNumbers.push(...item.toothNumbers);
        }
      });
      return allNumbers.filter((num) => num !== null);
    },
  },
  created() {
    this.uiHandler = this.createUiHandler();

    // zh locale인 경우 초기 데이터의 이미지를 중국어 버전으로 변경
    if (this.$i18n.locale === "zh") {
      this.companiesByApi.certifiedCompanies.forEach((company) => {
        if (company.name === "덴탈맥스" || company.name === "DENTALMAX") {
          company.imgUrl =
            "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese.png";
        }
      });
    }
  },
  mounted() {
    console.log("[Step3 mounted] initialData:", this.initialData);
    this.loadManufacturerList();
    this.restoreInitialData();
  },
  methods: {
    createUiHandler() {
      return {
        updateInputToothInfo: (updatedInfo) => {
          this.inputToothInfo = { ...updatedInfo };
        },
        updateConfirmedToothInfo: (updatedInfo) => {
          if (
            this.currentConfirmedIndex >= 0 &&
            this.currentConfirmedIndex < this.confirmedTeeth.length
          ) {
            this.confirmedTeeth.splice(this.currentConfirmedIndex, 1, {
              ...updatedInfo,
            });
          }
        },
        confirmAndAddCard: async () => {
          const hasToothNumber =
            this.inputToothInfo.toothNumbers &&
            this.inputToothInfo.toothNumbers.length > 0;

          if (!hasToothNumber) {
            this.$message.warning(this.$t("workorder_err_tooth_msg"));
            return;
          }
          if (!this.inputToothInfo.treatmentType) {
            this.$message.warning(this.$t("workorder_err_surgery_msg"));
            return;
          }
          if (!this.inputToothInfo.group) {
            this.$message.warning(this.$t("workorder_err_manufacturer_msg"));
            return;
          }

          // 덴탈맥스 직접 입력 모드에서 GTIN 코드 필수 검증
          if (
            this.inputToothInfo.group === "덴탈맥스" &&
            this.inputToothInfo.inputMode === "manual" &&
            !this.inputToothInfo.productInfoWithCode?.gtinCode
          ) {
            this.$message.warning(this.$t("workorder_err_gtin_required"));
            return;
          }

          const confirmedData = {
            ...this.inputToothInfo,
            toothNumbers: [...this.inputToothInfo.toothNumbers],
          };

          this.inputToothInfo = createProstheticTooth();
          await this.$nextTick();

          this.confirmedTeeth.push(confirmedData);
          this.currentConfirmedIndex = this.confirmedTeeth.length - 1;
          this.inputCardKey++;
        },
        switchConfirmedCard: (index) => {
          this.currentConfirmedIndex = index;
        },
        removeConfirmedCard: (index) => {
          this.confirmedTeeth.splice(index, 1);
          if (this.currentConfirmedIndex >= this.confirmedTeeth.length) {
            this.currentConfirmedIndex = Math.max(
              0,
              this.confirmedTeeth.length - 1
            );
          }
        },
      };
    },

    getCardTabLabel(tooth, index) {
      if (tooth.toothNumbers && tooth.toothNumbers.length > 0) {
        const sorted = [...tooth.toothNumbers].sort((a, b) => a - b);
        return this.$t("workorder_tooth_tab", { no: sorted.join(", ") });
      }
      return this.$t("workorder_tooth_tab_index", { index: index + 1 });
    },

    getOtherConfirmedTeethNumbers(currentIndex) {
      const otherNumbers = [];
      this.confirmedTeeth.forEach((tooth, index) => {
        if (
          index !== currentIndex &&
          tooth.toothNumbers &&
          tooth.toothNumbers.length > 0
        ) {
          otherNumbers.push(...tooth.toothNumbers);
        }
      });
      return otherNumbers;
    },

    updateModalShowHistory(value) {
      this.modals.history = {
        ...this.modals.history,
        ...value,
      };
    },

    async loadManufacturerList() {
      try {
        this.$store.commit("setIsLoading", true);
        const response = await apiInstance.get(
          "/accounts/issue/manufacturers/DENT/ZIRCONIA"
        );

        if (response.data && response.data.length > 0) {
          const certifiedCompanies = response.data.filter(
            (item) => item.isCertified
          );
          const notCertifiedCompanies = response.data.filter(
            (item) => !item.isCertified
          );

          // zh locale인 경우 중국어 이미지 사용
          if (this.$i18n.locale === "zh") {
            certifiedCompanies.forEach((company) => {
              if (company.name === "덴탈맥스" || company.name === "DENTALMAX") {
                company.imgUrl =
                  "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese.png";
              }
            });
          }

          this.companiesByApi.certifiedCompanies = certifiedCompanies;
          this.companiesByApi.notCertifiedCompanies = notCertifiedCompanies;
        }
      } catch (error) {
        console.error("제조사 목록 로드 실패:", error);
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },

    updateManufacturerList(result) {
      const certifiedCompanies = result.filter((item) => item.isCertified);
      const notCertifiedCompanies = result.filter((item) => !item.isCertified);

      // zh locale인 경우 중국어 이미지 사용
      if (this.$i18n.locale === "zh") {
        certifiedCompanies.forEach((company) => {
          if (company.name === "덴탈맥스" || company.name === "DENTALMAX") {
            company.imgUrl =
              "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese.png";
          }
        });
      }

      this.companiesByApi.certifiedCompanies = certifiedCompanies;
      this.companiesByApi.notCertifiedCompanies = notCertifiedCompanies;
    },

    async validateForm() {
      // 플래그 초기화
      this.includeInputInPublish = false;

      const hasConfirmedTeeth = this.confirmedTeeth.length > 0;
      const hasInputData =
        (this.inputToothInfo.toothNumbers &&
          this.inputToothInfo.toothNumbers.length > 0) ||
        this.inputToothInfo.treatmentType;
      const isInputComplete =
        this.inputToothInfo.toothNumbers &&
        this.inputToothInfo.toothNumbers.length > 0 &&
        this.inputToothInfo.treatmentType &&
        this.inputToothInfo.group &&
        this.inputToothInfo.itemName;

      // 케이스 1: 확정된 카드도 없고 입력 폼도 비어있음
      if (!hasConfirmedTeeth && !hasInputData) {
        await oneBtnDialog(
          this.$t("workorder_err_tooth_title"),
          this.$t("workorder_select_tooth"),
          false,
          this.$t("workorder_btn_confirm")
        );
        return false;
      }

      // 케이스 2: 확정된 카드가 있고, 입력 폼에도 작성중인 데이터가 있음
      if (hasConfirmedTeeth && hasInputData) {
        if (isInputComplete) {
          // 완전한 데이터가 있으면 확인 메시지
          const reverseButtons = false;
          const response = await twoBtnDialog(
            this.$t("workorder_modal_data_check_title"),
            this.$t("workorder_modal_data_check_msg"),
            this.$t("workorder_modal_btn_include"),
            this.$t("workorder_modal_btn_confirmed_only"),
            reverseButtons
          );

          if (response) {
            // "함께 발행" 선택 (confirmButton)
            this.includeInputInPublish = true;
          } else {
            // "확정된 것만 발행" 선택 (cancelButton)
            this.includeInputInPublish = false;
          }
        } else {
          const reverseButtons = false;
          // 불완전한 데이터가 있을 때도 선택권 제공
          const response = await twoBtnDialog(
            this.$t("workorder_modal_data_check_title"),
            this.$t("workorder_modal_data_partial_msg"),
            this.$t("workorder_modal_btn_include"),
            this.$t("workorder_modal_btn_confirmed_only"),
            reverseButtons
          );

          if (response) {
            // "함께 발행" 선택 (confirmButton) - 입력 폼 데이터 검증
            if (
              !this.inputToothInfo.toothNumbers ||
              this.inputToothInfo.toothNumbers.length === 0
            ) {
              await oneBtnDialog(
                this.$t("workorder_err_tooth_title"),
                this.$t("workorder_err_tooth_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.inputToothInfo.treatmentType) {
              await oneBtnDialog(
                this.$t("workorder_err_surgery_title"),
                this.$t("workorder_err_surgery_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.inputToothInfo.group) {
              await oneBtnDialog(
                this.$t("workorder_err_manufacturer_title"),
                this.$t("workorder_err_manufacturer_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.inputToothInfo.itemName) {
              if (this.inputToothInfo.gtinSearchError) {
                await oneBtnDialog(
                  this.$t("workorder_err_barcode_title"),
                  this.$t("workorder_err_barcode_msg"),
                  false,
                  this.$t("workorder_btn_confirm")
                );
              } else {
                await oneBtnDialog(
                  this.$t("workorder_err_product_title"),
                  this.$t("workorder_err_product_msg"),
                  false,
                  this.$t("workorder_btn_confirm")
                );
              }
              return false;
            }
            this.includeInputInPublish = true;
          } else {
            // "확정된 것만 발행" 선택 (cancelButton)
            this.includeInputInPublish = false;
          }
        }
      }

      // 케이스 3: 확정된 카드가 없고 입력 폼에만 데이터가 있음
      if (!hasConfirmedTeeth && hasInputData) {
        // 입력 폼 데이터 검증
        if (
          !this.inputToothInfo.toothNumbers ||
          this.inputToothInfo.toothNumbers.length === 0
        ) {
          await oneBtnDialog(
            this.$t("workorder_err_tooth_title"),
            this.$t("workorder_err_tooth_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.inputToothInfo.treatmentType) {
          await oneBtnDialog(
            this.$t("workorder_err_surgery_title"),
            this.$t("workorder_err_surgery_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.inputToothInfo.group) {
          await oneBtnDialog(
            this.$t("workorder_err_manufacturer_title"),
            this.$t("workorder_err_manufacturer_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.inputToothInfo.itemName) {
          if (this.inputToothInfo.gtinSearchError) {
            await oneBtnDialog(
              this.$t("workorder_err_barcode_title"),
              this.$t("workorder_err_barcode_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          } else {
            await oneBtnDialog(
              this.$t("workorder_err_product_title"),
              this.$t("workorder_err_product_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          }
          return false;
        }
        // 자동으로 발행하겠다고 간주
        this.includeInputInPublish = true;
      }

      // 확정된 카드들 검증
      for (const tooth of this.confirmedTeeth) {
        if (!tooth.group) {
          await oneBtnDialog(
            this.$t("workorder_err_manufacturer_title"),
            this.$t("workorder_err_manufacturer_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!tooth.itemName && !tooth.productInfoWithoutCode?.brand) {
          if (tooth.gtinSearchError) {
            await oneBtnDialog(
              this.$t("workorder_err_barcode_title"),
              this.$t("workorder_err_barcode_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          } else {
            await oneBtnDialog(
              this.$t("workorder_err_product_title"),
              this.$t("workorder_err_product_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          }
          return false;
        }
      }

      return true;
    },

    getFormData() {
      console.log("[getFormData] confirmedTeeth:", this.confirmedTeeth);
      console.log("[getFormData] inputToothInfo:", this.inputToothInfo);

      const teethToProcess = [...this.confirmedTeeth];

      if (
        this.includeInputInPublish &&
        this.inputToothInfo.toothNumbers &&
        this.inputToothInfo.toothNumbers.length > 0
      ) {
        teethToProcess.push(this.inputToothInfo);
      }

      const allSelectedTeeth = [];
      teethToProcess.forEach((tooth) => {
        if (tooth.toothNumbers && tooth.toothNumbers.length > 0) {
          allSelectedTeeth.push(...tooth.toothNumbers);
        }
      });

      // 카드별로 묶어서 전송 + 작성중인 데이터도 저장
      const result = {
        prostheticType:
          teethToProcess[0]?.treatmentType?.toLowerCase() || "crown",
        selectedTeeth: allSelectedTeeth,
        toothDetails: teethToProcess.map((tooth) => ({
          toothNumbers: tooth.toothNumbers || [],
          treatmentType: tooth.treatmentType,
          manufacturer: tooth.group,
          manufacturerId: tooth.manufacturerId || "",
          detailSelection: tooth.detailSelection || "ZIRCONIA",
          product: tooth.itemName || tooth.productInfoWithoutCode?.brand || "",
          stm: tooth.switches?.stm || false,
          gtinCode: tooth.productInfoWithCode?.gtinCode || "",
          manufacturingDate: tooth.productInfoWithCode?.manufacturingDate || "",
          lotNumber: tooth.productInfoWithCode?.lotNumber || "",
        })),
        // 작성중인 데이터도 함께 저장 (Step4에서 돌아올 때 복원용)
        inputToothInfo: { ...this.inputToothInfo },
      };

      console.log("[getFormData] result:", result);
      return result;
    },

    restoreInitialData() {
      console.log("[restoreInitialData] 시작");
      console.log("[restoreInitialData] initialData:", this.initialData);
      console.log(
        "[restoreInitialData] toothDetails:",
        this.initialData?.toothDetails
      );
      console.log(
        "[restoreInitialData] inputToothInfo:",
        this.initialData?.inputToothInfo
      );

      // 작성중인 데이터(inputToothInfo) 복원
      if (this.initialData?.inputToothInfo) {
        console.log(
          "[Step3] 작성중인 데이터 복원:",
          this.initialData.inputToothInfo
        );
        this.inputToothInfo = { ...this.initialData.inputToothInfo };
      }

      // 확정된 카드들(toothDetails) 복원 - 단, inputToothInfo에 포함된 데이터는 제외
      if (
        this.initialData &&
        this.initialData.toothDetails &&
        this.initialData.toothDetails.length > 0
      ) {
        console.log(
          "[Step3] 확정 데이터 복원 실행:",
          this.initialData.toothDetails
        );

        // inputToothInfo와 동일한 데이터는 제외하고 확정된 카드만 복원
        const inputToothNumbers =
          this.initialData.inputToothInfo?.toothNumbers || [];
        const confirmedDetails = this.initialData.toothDetails.filter(
          (detail) => {
            // 작성중인 데이터와 동일한 치아번호 조합이면 제외
            const detailNumbers = detail.toothNumbers || [];
            return !this.arraysEqual(detailNumbers, inputToothNumbers);
          }
        );

        this.confirmedTeeth = confirmedDetails.map((detail) => ({
          toothNumbers: detail.toothNumbers || [],
          treatmentType: detail.treatmentType,
          group: detail.manufacturer,
          detailSelection: detail.detailSelection || "ZIRCONIA",
          itemName: detail.product,
          isCertified: true,
          willTypeProductInfoStepOne: false,
          manufacturerId: detail.manufacturerId || "",
          productInfoWithoutCode: {
            brand: detail.product,
            customBrand: "",
            spec: "",
            customSpec: "",
          },
          productInfoWithCode: {
            id: "",
            productCode: detail.gtinCode || "",
            itemName: detail.product,
            description: "",
            gtinCode: detail.gtinCode || "",
            manufacturingDate: detail.manufacturingDate || "",
            lotNumber: detail.lotNumber || "",
          },
          switches: {
            stm: detail.stm || false,
          },
        }));

        console.log("[Step3] 복원된 confirmedTeeth:", this.confirmedTeeth);

        if (this.confirmedTeeth.length > 0) {
          this.currentConfirmedIndex = 0;
        }
      } else {
        console.log("[Step3] 복원할 확정 데이터가 없음");
      }
    },

    // 배열이 동일한지 확인하는 헬퍼 함수
    arraysEqual(a, b) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort((x, y) => x - y);
      const sortedB = [...b].sort((x, y) => x - y);
      return sortedA.every((val, index) => val === sortedB[index]);
    },
  },
};
</script>

<style scoped lang="scss">
.prosthetic-v2-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
}

.input-card-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-button-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  background-color: white;
  margin-top: -16px;
  padding-top: 0;
}

.add-card-button {
  min-width: 600px;
  height: 48px;
  color: #17191c;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #1a6fcc;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 138, 236, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

.confirmed-cards-section {
  margin-top: 24px;
}

// 탭 컨테이너
.card-tabs-container {
  display: flex;
  overflow-x: auto;
  gap: 10px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
}

// 개별 탭 버튼
.card-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #eff4ff;
  border: 2px solid transparent;
  border-radius: 0px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    background-color: #e5e7eb;
    color: #374151;
  }

  &.active {
    background-color: #fff;
    font-weight: 600;

    .tab-label {
      color: #000;
    }
  }
}

.tab-label {
  flex: 1;
  color: inherit;
}

.tab-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 12px;
    color: #9ca3af;
  }

  &:hover {
    background-color: rgba(239, 68, 68, 0.1);

    i {
      color: #ef4444;
    }
  }
}
</style>
